import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Article } from '../../types';
import { articlesService } from '../../services/api';
import { nameInitials, nameLastInitial, tagHashtag } from '../../utils/blogFormat';
import BlogIcon from './BlogIcon';
import { useBlogSettings } from './blogSettings';

/* ───────────── برچسب‌ها ───────────── */
export const ArticleTags: React.FC<{ tags: string[] }> = ({ tags }) => {
  const { tagsTitle } = useBlogSettings();
  if (!tags.length) return null;
  return (
    <section aria-label={tagsTitle} className="flex flex-col gap-[10px] border-t border-[#e9e8e5] pt-4 lg:gap-2 lg:border-0 lg:pt-0">
      <span className="text-[12px] font-medium leading-5 text-[#5b5f66]">{tagsTitle}</span>
      <div className="flex flex-wrap gap-2">
        {tags.map((tag) => (
          <Link
            key={tag}
            to={`/articles?tag=${encodeURIComponent(tag)}`}
            className="rounded-full border border-[#e9e8e5] bg-[#f4f3f1] px-3 py-[6px] text-[12px] font-medium leading-5 text-[#5b5f66] transition-colors hover:bg-[#efdbff] hover:text-[#331751] lg:rounded-[6px] lg:border-0 lg:bg-[#f4f1ea] lg:px-3 lg:py-1"
          >
            <span className="lg:hidden">{tagHashtag(tag)}</span>
            <span className="hidden lg:inline">{tagHashtag(tag, true)}</span>
          </Link>
        ))}
      </div>
    </section>
  );
};

/* ───────────── بازخورد «مفید بود؟» + بوکمارک ───────────── */
const FEEDBACK_KEY = 'blog-feedback';
const BOOKMARK_KEY = 'blog-bookmarks';

const readJson = <T,>(key: string, fallback: T): T => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
};
const writeJson = (key: string, value: unknown) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* حافظهٔ محلی در دسترس نیست */
  }
};

export const HelpfulFeedback: React.FC<{ article: Article }> = ({ article }) => {
  const { feedbackTitle, feedbackSubtitle } = useBlogSettings();
  const [vote, setVote] = useState<'yes' | 'no' | null>(null);
  const [bookmarked, setBookmarked] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setVote(readJson<Record<string, 'yes' | 'no'>>(FEEDBACK_KEY, {})[article.id] || null);
    setBookmarked(readJson<string[]>(BOOKMARK_KEY, []).includes(article.id));
  }, [article.id]);

  const sendVote = async (helpful: boolean) => {
    if (vote || busy) return;
    setBusy(true);
    try {
      await articlesService.sendFeedback(article.id, helpful);
    } catch {
      /* ثبت محلی حتی اگر سرور در دسترس نبود */
    } finally {
      const next = helpful ? 'yes' : 'no';
      setVote(next);
      writeJson(FEEDBACK_KEY, { ...readJson<Record<string, string>>(FEEDBACK_KEY, {}), [article.id]: next });
      setBusy(false);
    }
  };

  const toggleBookmark = () => {
    const list = readJson<string[]>(BOOKMARK_KEY, []);
    const next = list.includes(article.id) ? list.filter((id) => id !== article.id) : [...list, article.id];
    writeJson(BOOKMARK_KEY, next);
    setBookmarked(next.includes(article.id));
  };

  // طبق طرح متن «بله» در موبایل تیره‌تر است
  const btn = (active: boolean, idleText = 'text-[#5b5f66]') =>
    `inline-flex min-h-[44px] flex-1 items-center justify-center gap-[6px] rounded-[8px] border px-4 py-[9px] text-[14px] font-medium leading-6 transition-colors lg:min-h-0 lg:flex-none lg:py-2 ${
      active ? 'border-[#4a2e68] bg-[#efdbff] text-[#331751]' : `border-[#e9e8e5] bg-[#f4f3f1] hover:bg-[#efeeeb] ${idleText}`
    } ${vote ? 'cursor-default' : ''}`;

  return (
    <section
      aria-label="بازخورد مقاله"
      className="flex flex-col gap-3 rounded-[12px] border border-[#e9e8e5] bg-[#fdfbf7] p-4 lg:flex-row lg:items-center lg:justify-between lg:bg-white lg:p-6 lg:drop-shadow-[0px_1px_1px_rgba(0,0,0,0.05)]"
    >
      <div className="flex flex-col gap-1 text-center lg:text-right">
        <h2 className="text-[18px] font-semibold leading-[30px] text-[#331751] lg:text-[#1a1c1a]">{vote ? 'ممنون از بازخورد شما' : feedbackTitle}</h2>
        {feedbackSubtitle && <p className="hidden text-[14px] leading-[26px] text-[#5b5f66] lg:block">{feedbackSubtitle}</p>}
      </div>
      <div className="flex items-center gap-3 lg:gap-2">
        <button type="button" onClick={() => sendVote(true)} disabled={busy} aria-pressed={vote === 'yes'} className={btn(vote === 'yes', 'text-[#1a1c1a] lg:text-[#5b5f66]')}>
          بله، مفید بود
          <BlogIcon name="thumbs-up" width={15.75} height={15} />
        </button>
        <button type="button" onClick={() => sendVote(false)} disabled={busy} aria-pressed={vote === 'no'} className={btn(vote === 'no')}>
          خیر
          <BlogIcon name="thumbs-down" width={15.75} height={15} />
        </button>
        <button
          type="button"
          onClick={toggleBookmark}
          aria-pressed={bookmarked}
          aria-label={bookmarked ? 'حذف از نشان‌شده‌ها' : 'نشان کردن مقاله'}
          className={`hidden flex-col items-center justify-center self-stretch rounded-[8px] border px-2 pb-[17px] pt-2 transition-colors lg:inline-flex ${bookmarked ? 'border-[#4a2e68] bg-[#efdbff]' : 'border-[#e9e8e5] bg-[#f4f3f1] hover:bg-[#efeeeb]'}`}
        >
          <BlogIcon name="bookmark" width={11.667} height={15} />
        </button>
      </div>
    </section>
  );
};

/* ───────────── باکس نویسنده ───────────── */
export const AuthorBox: React.FC<{ article: Article }> = ({ article }) => {
  const name = article.author || 'تحریریه من حقیقی';
  return (
    <section aria-label="دربارهٔ نویسنده" className="flex items-start gap-3 rounded-[12px] border border-[#e9e8e5] bg-[#f4f3f1] p-4 lg:gap-4 lg:p-6">
      <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#4a2e68] text-[18px] font-semibold leading-[30px] text-white drop-shadow-[0px_1px_1px_rgba(0,0,0,0.05)] lg:h-14 lg:w-14 lg:pb-[10.5px] lg:pt-[9.5px] lg:text-[22px] lg:font-bold lg:leading-9 lg:drop-shadow-none">
        {article.authorAvatar ? (
          <img src={article.authorAvatar} alt={name} className="h-full w-full object-cover" />
        ) : (
          <>
            <span className="lg:hidden">{nameLastInitial(name)}</span>
            <span className="hidden lg:inline">{nameInitials(name)}</span>
          </>
        )}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1 lg:gap-[5px]">
        <div className="flex flex-wrap items-center justify-between gap-2">
          {/* موبایل: آیکون سمت چپ نام، بدون سمت نویسنده (طبق طرح) */}
          <h2 className="inline-flex items-center gap-[6px] text-[18px] font-semibold leading-[30px] text-[#331751] lg:text-[#1a1c1a]">
            {name}
            <BlogIcon name="author-box" width={14.667} height={14} className="lg:hidden" />
          </h2>
          {article.authorRole && <span className="hidden text-[12px] font-medium leading-5 text-[#5b5f66] lg:block">{article.authorRole}</span>}
        </div>
        {article.authorBio && <p className="text-[14px] leading-[26px] text-[#5b5f66] lg:leading-[22.75px]">{article.authorBio}</p>}
      </div>
    </section>
  );
};

/* ───────────── جداکنندهٔ تزئینی ❦ ───────────── */
export const OrnamentDivider: React.FC = () => (
  <div className="hidden items-center justify-center gap-4 py-4 lg:flex" aria-hidden>
    <span className="h-px w-16 bg-[#e9e8e5]" />
    <span className="text-[14px] leading-5 text-[#5b5f66]" style={{ fontFamily: 'serif' }}>
      ❦
    </span>
    <span className="h-px w-16 bg-[#e9e8e5]" />
  </div>
);
